import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import eulaService from '../../services/eulaService';

const EulaModal = ({ isOpen, onAccept, onDecline }) => {
  const [eula, setEula] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadEula();
    }
  }, [isOpen]);

  const loadEula = async () => {
    try {
      setLoading(true);
      setError(null);
      const eulaData = await eulaService.getCurrentEula();
      setEula(eulaData);
    } catch (err) {
      console.error('Error loading EULA:', err);
      setError('Failed to load End User License Agreement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const element = e.target;
    const bottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 10;
    if (bottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAccept = async () => {
    if (!eula) return;

    try {
      setAccepting(true);
      await eulaService.acceptEula(eula.id);
      onAccept();
    } catch (err) {
      console.error('Error accepting EULA:', err);
      setError('Failed to accept EULA. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-2xl font-semibold text-gray-900">
              End User License Agreement
            </h2>
            {eula && (
              <p className="mt-1 text-sm text-gray-600">
                Version {eula.version} • Effective {new Date(eula.effective_date).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
              </div>
            ) : error ? (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                    <div className="mt-4">
                      <button
                        onClick={loadEula}
                        className="rounded-md bg-red-100 px-3 py-2 text-sm font-semibold text-red-800 hover:bg-red-200"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : eula ? (
              <>
                {/* Scrollable content area */}
                <div
                  className="prose prose-sm max-w-none max-h-96 overflow-y-auto border border-gray-200 rounded-md p-6 bg-gray-50"
                  onScroll={handleScroll}
                >
                  <ReactMarkdown>{eula.content}</ReactMarkdown>
                </div>

                {/* Scroll indicator */}
                {!hasScrolledToBottom && (
                  <div className="mt-2 text-sm text-amber-600 flex items-center">
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    Please scroll down to read the entire agreement
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                No EULA available
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                You must accept the agreement to continue using Just Practice
              </p>
              <div className="flex space-x-3">
                {onDecline && (
                  <button
                    onClick={onDecline}
                    disabled={accepting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
                  >
                    Decline
                  </button>
                )}
                <button
                  onClick={handleAccept}
                  disabled={!hasScrolledToBottom || accepting || !eula}
                  className="px-6 py-2 text-sm font-medium text-white bg-yellow-500 rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {accepting ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Accepting...
                    </span>
                  ) : (
                    'I Accept'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EulaModal;
